// One read and one write over every setting a tenant owns. Each value is still stored and validated by its own service.
import { z } from "zod";
import { AdGuardSettingsDto, AdGuardSettingsPatch } from "./ads";
import { AiSettingsDto, AiSettingsPut } from "./ai";
import { TenantDto, TenantPatch } from "./identity";
import { BrandInput, ResolvedBrandDto } from "./knowledge";
import { LeadSettingsDto, LeadSettingsPatch } from "./leads";
import { ChannelSettingsInputDto, CountrySettingsDto } from "./messaging";
import { ChannelDto, ChannelUpdate } from "./social";
import { ArchetypeAutonomyDto, SetArchetypeAutonomyRequest, StudioSettingsDto, UpdateStudioSettingsRequest } from "./studio";
import { PlanSettingsDto, PlanSettingsPut } from "./topics";

export const SETTING_SOURCES = ["tenant", "agency", "platform"] as const;
export const SettingSource = z.enum(SETTING_SOURCES);
export type SettingSource = z.infer<typeof SettingSource>;

/** `source` says where `effective` came from. `inherited` is what the tenant would fall back to; null when there is no agency default. */
const Section = <T extends z.ZodType>(value: T) => z.object({ effective: value, inherited: value.nullable(), source: SettingSource });

const IDENTITY_FIELDS = { name: true, country: true, languages: true, timezone: true, vertical: true } as const;

export const TenantSettingsDto = z.object({
  /** Name, country, languages, timezone, vertical. */
  identity: Section(TenantDto.pick(IDENTITY_FIELDS)),
  /** The one group with real agency defaults; `effective.sources` says field by field who set it. */
  brand: z.object({ effective: ResolvedBrandDto, inherited: BrandInput.nullable(), stored: BrandInput.nullable(), source: SettingSource }),
  channels: Section(z.array(ChannelDto)),
  /** Per country: the rules, quiet hours and sending caps. */
  messaging: Section(z.array(CountrySettingsDto)),
  /** When the week is planned and how much goes out. */
  planning: Section(PlanSettingsDto),
  autonomy: Section(StudioSettingsDto),
  archetypes: Section(z.array(ArchetypeAutonomyDto)),
  /** Spend caps. */
  ads: Section(AdGuardSettingsDto),
  /** Models, effort and the monthly cap. */
  ai: Section(AiSettingsDto),
  leads: Section(LeadSettingsDto),
});
export type TenantSettingsDto = z.infer<typeof TenantSettingsDto>;

export const TenantSettingsPatch = z
  .strictObject({
    identity: TenantPatch.pick(IDENTITY_FIELDS),
    /** Only the fields given change; the rest of the stored brand stays. */
    brand: BrandInput,
    channels: z.array(z.strictObject({ id: z.uuid(), patch: ChannelUpdate.omit({ config: true }).strict() })).min(1).max(50),
    messaging: z.array(z.strictObject({ country: z.string().trim().length(2), settings: ChannelSettingsInputDto })).min(1).max(30),
    /** The Ad Library token is a credential and keeps its own route. */
    planning: PlanSettingsPut.omit({ adLibraryToken: true }),
    autonomy: UpdateStudioSettingsRequest,
    archetypes: z.array(SetArchetypeAutonomyRequest.extend({ archetype: z.string().trim().min(1).max(64) })).min(1).max(100),
    ads: AdGuardSettingsPatch,
    ai: AiSettingsPut,
    leads: LeadSettingsPatch,
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: "at least one section is required" });
export type TenantSettingsPatch = z.input<typeof TenantSettingsPatch>;

export const TENANT_SETTINGS_SECTIONS = ["identity", "brand", "channels", "messaging", "planning", "autonomy", "archetypes", "ads", "ai", "leads"] as const;
export const TenantSettingsSection = z.enum(TENANT_SETTINGS_SECTIONS);
export type TenantSettingsSection = z.infer<typeof TenantSettingsSection>;

/** The sections do not share a transaction: `failed` says which did not change, and everything else did. */
export const TenantSettingsPatchResponse = z.object({
  settings: TenantSettingsDto,
  applied: z.array(TenantSettingsSection),
  failed: z.array(z.object({ section: TenantSettingsSection, code: z.string(), details: z.unknown().optional() })),
});
export type TenantSettingsPatchResponse = z.infer<typeof TenantSettingsPatchResponse>;
